<template>
  <div class="d-flex flex-column justify-content-start" style="height: 100%">
    <v-card style="height: 100%" rounded elevation="2">
      <v-card-title class="text-h5 font-weight-bold primary--text">
        My Courses
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text>
        <!-- Resolution Info Banner -->
        <v-alert
          v-if="resolutionSource"
          :type="resolutionSource === 'LEGACY' ? 'warning' : 'info'"
          dense
          outlined
          class="mb-4"
        >
          <div class="d-flex align-center justify-space-between flex-wrap">
            <div>
              <strong>{{ getResolutionLabel }}</strong>
              <span v-if="curriculum" class="ml-2">- {{ curriculum.title }}</span>
            </div>
            <div class="text-caption">
              {{ totalCourses }} courses | {{ totalCreditUnits }} credit units
            </div>
          </div>
        </v-alert>

        <!-- Filters -->
        <v-row class="mb-4">
          <v-col cols="12" sm="6" md="4">
            <x-text-field
              v-model="search"
              placeholder="Search courses..."
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              clearable
              hide-details
            ></x-text-field>
          </v-col>
          <v-col cols="6" sm="3" md="2">
            <x-select
              v-model="filterLevel"
              :items="levelOptions"
              item-text="name"
              item-value="id"
              placeholder="Level"
              outlined
              dense
              clearable
              hide-details
            ></x-select>
          </v-col>
          <v-col cols="6" sm="3" md="2">
            <x-select
              v-model="filterSemester"
              :items="semesterOptions"
              placeholder="Semester"
              outlined
              dense
              clearable
              hide-details
            ></x-select>
          </v-col>
          <v-col cols="6" sm="3" md="2">
            <x-select
              v-model="filterType"
              :items="courseTypeOptions"
              placeholder="Type"
              outlined
              dense
              clearable
              hide-details
            ></x-select>
          </v-col>
        </v-row>

        <!-- Courses Table -->
        <v-data-table
          :headers="headers"
          :items="filteredCourses"
          :loading="loading"
          :search="search"
          :items-per-page="15"
          class="elevation-1"
          :footer-props="{
            'items-per-page-options': [10, 15, 25, 50, -1]
          }"
        >
          <template v-slot:[`item.courseCode`]="{ item }">
            <div v-if="item">
              <div class="font-weight-medium">{{ item.courseCode || '-' }}</div>
              <div class="text-caption text--secondary">{{ item.courseTitle || '' }}</div>
            </div>
          </template>

          <template v-slot:[`item.levelName`]="{ item }">
            {{ item && item.levelName ? item.levelName : '-' }}
          </template>

          <template v-slot:[`item.semesterIndex`]="{ item }">
            {{ item ? getSemesterName(item.semesterIndex) : '-' }}
          </template>

          <template v-slot:[`item.courseType`]="{ item }">
            <v-chip
              v-if="item && item.courseType"
              :color="getTypeColor(item.courseType)"
              small
              dark
              label
            >
              {{ item.courseType }}
            </v-chip>
            <span v-else>-</span>
          </template>

          <template v-slot:[`item.creditUnit`]="{ item }">
            <span class="font-weight-medium">{{ item ? item.creditUnit : '-' }}</span>
          </template>

          <template v-slot:no-data>
            <div class="text-center pa-4 grey--text">
              <v-icon size="48" color="grey lighten-1">mdi-book-open-variant</v-icon>
              <div class="mt-2">No courses found</div>
            </div>
          </template>
        </v-data-table>

        <!-- Summary Footer -->
        <v-row v-if="filteredCourses.length > 0" class="mt-4">
          <v-col cols="12">
            <v-sheet outlined rounded class="pa-3">
              <div class="d-flex flex-wrap justify-space-between">
                <div class="mr-4">
                  <span class="text-caption text--secondary">Filtered Courses:</span>
                  <span class="font-weight-bold ml-1">{{ filteredCourses.length }}</span>
                </div>
                <div class="mr-4">
                  <span class="text-caption text--secondary">Filtered Credit Units:</span>
                  <span class="font-weight-bold ml-1">{{ filteredCreditUnits }}</span>
                </div>
                <div>
                  <span class="text-caption text--secondary">Total Programme Credit Units:</span>
                  <span class="font-weight-bold ml-1">{{ totalCreditUnits }}</span>
                </div>
              </div>
            </v-sheet>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <loading-dialog :show="loading"></loading-dialog>
    <message-box
      :show="messageShow"
      :message="message"
      :type="messageType"
      v-on:close-message="messageShow = false"
    ></message-box>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Api from "@/main/api";
import ErrorResolver from "@/shared/ErrorResolver";
import LoadingDialog from "@ui/components/LoadingDialog";
import MessageBox from "@ui/components/MessageBox";
import XTextField from "@ui/widgets/XTextField.vue";
import XSelect from "@ui/widgets/XSelect.vue";

export default {
  name: "StudentCoursesPage",
  components: {
    LoadingDialog,
    MessageBox,
    XTextField,
    XSelect
  },

  data: () => ({
    loading: false,
    messageShow: false,
    message: "",
    messageType: "",

    courses: [],
    resolutionSource: null,
    curriculum: null,
    totalCourses: 0,
    totalCreditUnits: 0,

    search: "",
    filterLevel: null,
    filterSemester: null,
    filterType: null,

    semesterOptions: [
      { value: 0, text: "First Semester" },
      { value: 1, text: "Second Semester" }
    ],
    courseTypeOptions: ["COMPULSORY", "ELECTIVE", "REQUIRED"],

    headers: [
      { text: "Course", value: "courseCode", sortable: true },
      { text: "Level", value: "levelName", sortable: true },
      { text: "Semester", value: "semesterIndex", sortable: true },
      { text: "Type", value: "courseType", sortable: true },
      { text: "Credit Units", value: "creditUnit", sortable: true, align: "center" }
    ]
  }),

  computed: {
    ...mapGetters(["getUser"]),

    levelOptions() {
      const levels = new Set();
      this.courses.forEach(c => {
        if (c.levelName) {
          levels.add(c.levelName);
        }
      });
      return Array.from(levels).map(name => ({ id: name, name: name }));
    },

    filteredCourses() {
      if (!this.courses || !Array.isArray(this.courses)) return [];
      return this.courses.filter(item => {
        if (!item) return false;
        let matches = true;
        if (this.filterLevel && item.levelName !== this.filterLevel) {
          matches = false;
        }
        if (this.filterSemester !== null && this.filterSemester !== undefined && item.semesterIndex !== this.filterSemester) {
          matches = false;
        }
        if (this.filterType && item.courseType !== this.filterType) {
          matches = false;
        }
        return matches;
      });
    },

    filteredCreditUnits() {
      return this.filteredCourses.reduce((sum, c) => sum + (c.creditUnit || 0), 0);
    },

    getResolutionLabel() {
      const labels = {
        OVERRIDE: "Curriculum Override",
        YEAR_MAPPING: "Year-Based Curriculum",
        DEFAULT: "Default Curriculum",
        LEGACY: "Legacy Course List"
      };
      return labels[this.resolutionSource] || this.resolutionSource;
    }
  },

  created() {
    this.loadCourses();
  },

  methods: {
    showMsg(msg, type = "success") {
      this.message = msg;
      this.messageType = type;
      this.messageShow = true;
    },

    async loadCourses() {
      if (!this.getUser) return;

      this.loading = true;
      try {
        const response = await Api.curriculum.getStudentCourses(this.getUser.id);
        const data = response.data;

        this.courses = data.courses || [];
        this.resolutionSource = data.resolutionSource;
        this.curriculum = data.curriculum;
        this.totalCourses = data.totalCourses || this.courses.length;
        this.totalCreditUnits = data.totalCreditUnits || 0;
      } catch (error) {
        ErrorResolver.checkErrors(this, error);
      } finally {
        this.loading = false;
      }
    },

    getSemesterName(index) {
      if (index === null || index === undefined) return "-";
      if (index === 0) return "First Semester";
      if (index === 1) return "Second Semester";
      return `Semester ${index + 1}`;
    },

    getTypeColor(type) {
      switch (type) {
        case "COMPULSORY":
          return "green";
        case "ELECTIVE":
          return "blue";
        case "REQUIRED":
          return "orange";
        default:
          return "grey";
      }
    }
  }
};
</script>

<style scoped>
.v-data-table {
  border-radius: 8px;
}
</style>
